import React, { Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Outlet, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BellIcon,
  Bars3Icon,
  XMarkIcon
} from '@heroicons/react/24/outline';

// Eagerly loaded — needed for initial render
import ChatShell from './components/ChatShell.jsx';
import Dashboard from './components/Dashboard';
import Login from './components/Login';
import AuthCallback from './components/AuthCallback';
import Sidebar from './components/Sidebar';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './contexts/AuthContext.jsx';

// Retry wrapper for lazy imports — handles stale chunk references after deploys
function lazyRetry(importFn) {
  return React.lazy(() =>
    importFn().catch(() => {
      // On chunk load failure, try once with cache bust
      return new Promise((resolve) => {
        const hasReloaded = sessionStorage.getItem('chunk-reload');
        if (!hasReloaded) {
          sessionStorage.setItem('chunk-reload', '1');
          window.location.reload();
        }
        resolve(importFn());
      });
    })
  );
}

// Lazy loaded — split into separate chunks, loaded on navigation
const RiskLibrary = lazyRetry(() => import('./components/RiskLibrary'));
const Settings = lazyRetry(() => import('./components/Settings'));
const Taxonomy = lazyRetry(() => import('./components/Taxonomy'));
const TaxonomyDetail = lazyRetry(() => import('./components/TaxonomyDetail'));
const IssueLibrary = lazyRetry(() => import('./components/IssueLibrary'));
const ActionLibrary = lazyRetry(() => import('./components/ActionLibrary'));
const IssueDetail = lazyRetry(() => import('./components/IssueDetail'));
const OrganisationSettings = lazyRetry(() => import('./components/OrganisationSettings'));
const UsersSettings = lazyRetry(() => import('./components/UsersSettings'));
const InventorySettings = lazyRetry(() => import('./components/InventorySettings'));
const RiskSettings = lazyRetry(() => import('./components/RiskSettings'));
const AnalyticsSettings = lazyRetry(() => import('./components/AnalyticsSettings'));
const ComplianceSettings = lazyRetry(() => import('./components/ComplianceSettings'));
const FrameworkDetailView = lazyRetry(() => import('./components/FrameworkDetailView'));
const IncidentsSettings = lazyRetry(() => import('./components/IncidentsSettings'));
const IssuesSettings = lazyRetry(() => import('./components/IssuesSettings'));
const IssueRatingSettings = lazyRetry(() => import('./components/IssueRatingSettings'));
const TaxonomySettings = lazyRetry(() => import('./components/TaxonomySettings'));
const RiskDetail = lazyRetry(() => import('./components/RiskDetail'));
const IntentFeedbackReview = lazyRetry(() => import('./components/admin/IntentFeedbackReview'));
const AIAssistantSettings = lazyRetry(() => import('./components/admin/AIAssistantSettings'));
const AuditLogSettings = lazyRetry(() => import('./components/settings/AuditLogSettings'));
const APIKeysSettings = lazyRetry(() => import('./components/settings/APIKeysSettings'));
const WebhooksSettings = lazyRetry(() => import('./components/settings/WebhooksSettings'));
const DeveloperHub = lazyRetry(() => import('./components/DeveloperHub'));
const RiskFieldsAdmin = lazyRetry(() => import('./components/admin/RiskFieldsAdmin'));
const TaxonomyFieldsAdmin = lazyRetry(() => import('./components/admin/TaxonomyFieldsAdmin'));
const IssueFieldsAdmin = lazyRetry(() => import('./components/admin/IssueFieldsAdmin'));
const ActionFieldsAdmin = lazyRetry(() => import('./components/admin/ActionFieldsAdmin'));
const ActionDetails = lazyRetry(() => import('./components/ActionDetails'));
const ActionSettings = lazyRetry(() => import('./components/ActionSettings'));
const ControlLibrary = lazyRetry(() => import('./components/ControlLibrary'));
const ControlChain = lazyRetry(() => import('./components/ControlChain'));
const ControlDetail = lazyRetry(() => import('./components/ControlDetail'));
const ControlFieldsAdmin = lazyRetry(() => import('./components/admin/ControlFieldsAdmin'));
const SemanticTags = lazyRetry(() => import('./components/admin/SemanticTags'));
const ControlLatticeVisualization = lazyRetry(() => import('./components/ControlLatticeVisualization'));
const ControlLatticeDashboard = lazyRetry(() => import('./components/ControlLatticeDashboard'));
const ControlLatticeTest = lazyRetry(() => import('./components/ControlLatticeTest'));
const ControlFlowChart = lazyRetry(() => import('./components/ControlFlowChart'));
const ControlsDashboard = lazyRetry(() => import('./components/controls/ControlsDashboard'));
const AdvancedAnalyticsDashboard = lazyRetry(() => import('./components/AdvancedAnalyticsDashboard'));
const FloatingChatWidget = lazyRetry(() => import('./components/FloatingChatWidget.jsx'));
const ChatSlideIn = lazyRetry(() => import('./components/ChatSlideIn'));
const IntelligentDashboard = lazyRetry(() => import('./components/IntelligentDashboard'));
const AutomatedInsights = lazyRetry(() => import('./components/AutomatedInsights'));
const AloraIntelligence = lazyRetry(() => import('./components/AloraIntelligence'));
const SystemHealthMonitor = lazyRetry(() => import('./components/SystemHealthMonitor'));
const InteractiveIntelligenceDashboard = lazyRetry(() => import('./components/InteractiveIntelligenceDashboard'));
const JobsLibrary = lazyRetry(() => import('./components/JobsLibrary'));
const JobsKanban = lazyRetry(() => import('./components/JobsKanban'));
const JobDetail = lazyRetry(() => import('./components/JobDetail'));
const EnhancedJobsUI = lazyRetry(() => import('./components/enhanced-jobs/EnhancedJobsUI'));
const RegulatoryFrameworkManager = lazyRetry(() => import('./components/RegulatoryFrameworkManager'));
const ComplianceHub = lazyRetry(() => import('./components/compliance/ComplianceHub'));
const RegulationDiscoveryDemo = lazyRetry(() => import('./components/compliance/RegulationDiscoveryDemo'));
const IncidentLibrary = lazyRetry(() => import('./components/IncidentLibrary'));
const IncidentDetail = lazyRetry(() => import('./components/IncidentDetail'));
const IncidentExternalFeeds = lazyRetry(() => import('./components/IncidentExternalFeeds'));
const KRILibrary = lazyRetry(() => import('./components/KRILibrary'));
const KRIDetail = lazyRetry(() => import('./components/KRIDetail'));
const IncidentAnalyticsDashboard = lazyRetry(() => import('./components/IncidentAnalyticsDashboard'));
const RelationshipDomainDetection = lazyRetry(() => import('./components/RelationshipDomainDetection'));
const RiskHubs = lazyRetry(() => import('./components/RiskHubs'));
const OperationalRiskHub = lazyRetry(() => import('./components/hubs/OperationalRiskHub'));
const CyberRiskHub = lazyRetry(() => import('./components/hubs/CyberRiskHub'));
const FinancialRiskHub = lazyRetry(() => import('./components/hubs/FinancialRiskHub'));
const FinancialCrimeHub = lazyRetry(() => import('./components/hubs/FinancialCrimeHub'));
const ThirdPartyRiskHub = lazyRetry(() => import('./components/hubs/ThirdPartyRiskHub'));
const ESGClimateHub = lazyRetry(() => import('./components/hubs/ESGClimateHub'));
const StrategicRiskHub = lazyRetry(() => import('./components/hubs/StrategicRiskHub'));
const ModelRiskHub = lazyRetry(() => import('./components/hubs/ModelRiskHub'));
const RegulatoryChangeHub = lazyRetry(() => import('./components/hubs/RegulatoryChangeHub'));
const AssessmentDashboard = lazyRetry(() => import('./components/assessments/AssessmentDashboard'));
const CreateAssessment = lazyRetry(() => import('./components/assessments/CreateAssessment'));
const RiskAssessmentPeriodsSettings = lazyRetry(() => import('./components/RiskAssessmentPeriodsSettings'));
const PolicyForm = lazyRetry(() => import('./components/PolicyForm'));
const PolicyDetail = lazyRetry(() => import('./components/PolicyDetail'));
const TestingDashboard = lazyRetry(() => import('./components/testing/TestingDashboard'));
const AdvancedTestingAnalytics = lazyRetry(() => import('./components/testing/AdvancedTestingAnalytics'));
const CampaignManagement = lazyRetry(() => import('./components/testing/CampaignManagement'));
const RealTimeTestMonitor = lazyRetry(() => import('./components/testing/RealTimeTestMonitor'));
const TestPlansLibrary = lazyRetry(() => import('./components/testing/TestPlansLibrary'));
const TestExecution = lazyRetry(() => import('./components/testing/TestExecution'));
const RiskAssessmentDashboard = lazyRetry(() => import('./components/assessments/RiskAssessmentDashboard'));

// Protected Layout component that includes the sidebar and main content
function Layout() {
  const [sidebarOpen, setSidebarOpen] = React.useState(true);

  return (
    <div className="h-screen flex overflow-hidden bg-gray-50">
      {/* Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ x: -280 }}
            animate={{ x: 0 }}
            exit={{ x: -280 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="relative flex-shrink-0"
          >
            <Sidebar />
          </motion.div>
        )}
      </AnimatePresence>
      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <div className="flex items-center justify-between h-16 px-4 bg-white border-b border-gray-200">
          <div className="flex items-center">
            {!sidebarOpen && (
              <button
                onClick={() => setSidebarOpen(true)}
                className="mr-4 text-gray-400 hover:text-gray-500"
              >
                <Bars3Icon className="h-6 w-6" />
              </button>
            )}
            <h2 className="text-lg font-semibold text-gray-900">
              {/* Optionally show current route name here */}
            </h2>
          </div>
          <div className="flex items-center space-x-4">
            <button className="text-gray-400 hover:text-gray-500">
              <BellIcon className="h-6 w-6" />
            </button>
            <div className="h-8 w-8 rounded-full bg-primary-500 flex items-center justify-center text-white text-sm font-medium">
              U
            </div>
          </div>
        </div>
        {/* Content area */}
        <main className="flex-1 overflow-y-auto w-full max-w-none">
          <div className="w-full h-full">
            <Suspense fallback={
              <div className="flex items-center justify-center h-64">
                <div className="flex items-center gap-3">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                  <span className="text-gray-500 text-sm">Loading...</span>
                </div>
              </div>
            }>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>
      {/* Basic ChatSlideIn Implementation */}
      <ChatSlideIn />
    </div>
  );
}

// Create the router configuration
const router = createBrowserRouter([
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/auth/callback",
    element: <AuthCallback />,
  },
  {
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: "/",
        element: <Dashboard />,
      },
      {
        path: "/ai-assistant",
        element: (
          <ChatShell>
            {/* Placeholder for IssueAgent */}
          </ChatShell>
        ),
      },
      {
        path: "/risk-library",
        element: <RiskLibrary />,
      },
      {
        path: "/risk-details/:riskId",
        element: <RiskDetail />,
      },
      {
        path: "/taxonomy",
        element: <Taxonomy />,
      },
      {
        path: "/taxonomy-detail/:taxonomyId",
        element: <TaxonomyDetail />,
      },
      {
        path: "/taxonomy-detail",
        element: <TaxonomyDetail />,
      },
      {
        path: "/issue-library",
        element: <IssueLibrary />,
      },
      {
        path: "/action-library",
        element: <ActionLibrary />,
      },
      {
        path: "/issue-details/:issueId",
        element: <IssueDetail />,
      },
      {
        path: "/issue-details/new",
        element: <IssueDetail />,
      },
      {
        path: "/risk-details/new", 
        element: <RiskDetail />,
      },
      {
        path: "/analytics",
        element: <div>Analytics Page</div>,
      },
      {
        path: "/intelligent-dashboards",
        element: <IntelligentDashboard />,
      },
      {
        path: "/automated-insights",
        element: <AutomatedInsights />,
      },
      {
        path: "/alora-intelligence",
        element: <AloraIntelligence />,
      },
      {
        path: "/alora-intelligence/automated-insights",
        element: <AutomatedInsights />,
      },
      {
        path: "/alora-intelligence/system-health",
        element: <SystemHealthMonitor />,
      },
      {
        path: "/interactive-intelligence",
        element: <InteractiveIntelligenceDashboard />,
      },
      {
        path: "/settings",
        element: <Settings />,
      },
      {
        path: "/settings/organisation",
        element: <OrganisationSettings />,
      },
      {
        path: "/settings/users",
        element: <UsersSettings />,
      },
      {
        path: "/settings/inventory",
        element: <InventorySettings />,
      },
      {
        path: "/settings/risk",
        element: <RiskSettings />,
      },
      {
        path: "/settings/risk-assessment-periods",
        element: <RiskAssessmentPeriodsSettings />,
      },
      {
        path: "/settings/analytics",
        element: <AnalyticsSettings />,
      },

      {
        path: "/settings/compliance",
        element: <ComplianceSettings />,
      },
      {
        path: "/settings/incidents",
        element: <IncidentsSettings />,
      },
      {
        path: "/settings/issues",
        element: <IssuesSettings />,
      },
      {
        path: "/settings/taxonomy",
        element: <TaxonomySettings />,
      },
      {
        path: "/settings/ai-assistant",
        element: <AIAssistantSettings />,
      },
      {
        path: "/settings/semantic-tags",
        element: <SemanticTags />,
      },
      {
        path: "/settings/regulatory-frameworks",
        element: <RegulatoryFrameworkManager />,
      },
      {
        path: "/settings/audit-log",
        element: <AuditLogSettings />,
      },
      {
        path: "/settings/api-keys",
        element: <APIKeysSettings />,
      },
      {
        path: "/settings/webhooks",
        element: <WebhooksSettings />,
      },
      {
        path: "/help",
        element: <DeveloperHub />,
      },
      {
        path: "/developer",
        element: <DeveloperHub />,
      },
      {
        path: "/action-details/:id",
        element: <ActionDetails />,
      },
      {
        path: "/settings/actions",
        element: <ActionSettings />,
      },
      {
        path: "/settings/risk-fields",
        element: <RiskFieldsAdmin />,
      },
      {
        path: "/settings/taxonomy-fields",
        element: <TaxonomyFieldsAdmin />,
      },
      {
        path: "/settings/issue-fields",
        element: <IssueFieldsAdmin />,
      },
      {
        path: "/settings/action-fields",
        element: <ActionFieldsAdmin />,
      },
      {
        path: "/settings/issue-ratings",
        element: <IssueRatingSettings />,
      },
      {
        path: "/control-library",
        element: <ControlLibrary />,
      },
      {
        path: "/control-chain",
        element: <ControlChain />,
      },
      {
        path: "/control-details/:controlId",
        element: <ControlDetail />,
      },
      {
        path: "/control-details/new",
        element: <ControlDetail />,
      },
      {
        path: "/settings/control-fields",
        element: <ControlFieldsAdmin />,
      },
      {
        path: "/control-lattice",
        element: <ControlLatticeVisualization />,
      },
      {
        path: "/control-lattice-dashboard",
        element: <ControlLatticeDashboard />,
      },
      {
        path: "/control-flow-chart",
        element: <ControlFlowChart />,
      },
      {
        path: "/advanced-analytics",
        element: <AdvancedAnalyticsDashboard />,
      },
      {
        path: "/controls",
        element: <ControlsDashboard />,
      },
      {
        path: "/control-dashboard",
        element: <ControlsDashboard />,
      },
      {
        path: "/action-plans",
        element: <ActionLibrary />,
      },
      {
        path: "/jobs",
        element: <JobsLibrary />,
      },
      {
        path: "/jobs/enhanced",
        element: <EnhancedJobsUI />,
      },
      {
        path: "/jobs/:jobId",
        element: <JobDetail />,
      },
      {
        path: "/compliance/*",
        element: <ComplianceHub />,
      },
      {
        path: "/compliance-discovery-demo",
        element: <RegulationDiscoveryDemo />,
      },
      {
        path: "/incident-library",
        element: <IncidentLibrary />,
      },
      {
        path: "/incident-details/:incidentId",
        element: <IncidentDetail />,
      },
      {
        path: "/incident-details/new",
        element: <IncidentDetail />,
      },
      {
        path: "/incidents/external",
        element: <IncidentExternalFeeds />,
      },
      {
        path: "/incidents/analytics",
        element: <IncidentAnalyticsDashboard />,
      },
      {
        path: "/kri-library",
        element: <KRILibrary />,
      },
      {
        path: "/kri-details/:kriId",
        element: <KRIDetail />,
      },
      {
        path: "/relationship-domain-detection",
        element: <RelationshipDomainDetection />,
      },
      {
        path: "/policies",
        element: <Navigate to="/compliance/policies" replace />,
      },
      {
        path: "/policies/create",
        element: <PolicyForm mode="create" />,
      },
      {
        path: "/policies/:id",
        element: <PolicyDetail />,
      },
      {
        path: "/policies/:id/edit",
        element: <PolicyForm mode="edit" />,
      },
      // Risk Assessment Module Routes
      {
        path: "/assessments",
        element: <AssessmentDashboard />,
      },
      {
        path: "/assessments/create",
        element: <CreateAssessment />,
      },
      {
        path: "/assessments/:id",
        element: <AssessmentDashboard />,
      },
      // Testing Module Routes
      {
        path: "/testing",
        element: <TestingDashboard />,
      },
      {
        path: "/testing/plans",
        element: <TestPlansLibrary />,
      },
      {
        path: "/testing/execution",
        element: <TestExecution />,
      },
      {
        path: "/testing/evidence",
        element: <div>Evidence Management (Coming Soon)</div>,
      },
      {
        path: "/testing/campaigns",
        element: <CampaignManagement />,
      },
      {
        path: "/testing/monitor",
        element: <RealTimeTestMonitor />,
      },
      {
        path: "/testing/analytics",
        element: <AdvancedTestingAnalytics />,
      },
      // Risk Assessment Routes
      {
        path: "/assessments",
        element: <RiskAssessmentDashboard />,
      }
    ]
  },
  // Separate layout for Risk Hubs (no main sidebar)
  {
    path: "/risk-hubs",
    element: <RiskHubs />,
  },
  {
    path: "/risk-hubs/operational-risk",
    element: <OperationalRiskHub />,
  },
  {
    path: "/risk-hubs/operational-risk/*",
    element: <OperationalRiskHub />,
  },
  {
    path: "/risk-hubs/cyber-risk",
    element: <CyberRiskHub />,
  },
  {
    path: "/risk-hubs/cyber-risk/*",
    element: <CyberRiskHub />,
  },
  {
    path: "/risk-hubs/financial-risk",
    element: <FinancialRiskHub />,
  },
  {
    path: "/risk-hubs/financial-risk/*",
    element: <FinancialRiskHub />,
  },
  {
    path: "/risk-hubs/financial-crime",
    element: <FinancialCrimeHub />,
  },
  {
    path: "/risk-hubs/financial-crime/*",
    element: <FinancialCrimeHub />,
  },
  {
    path: "/risk-hubs/third-party-risk",
    element: <ThirdPartyRiskHub />,
  },
  {
    path: "/risk-hubs/third-party-risk/*",
    element: <ThirdPartyRiskHub />,
  },
  {
    path: "/risk-hubs/esg-climate",
    element: <ESGClimateHub />,
  },
  {
    path: "/risk-hubs/esg-climate/*",
    element: <ESGClimateHub />,
  },
  {
    path: "/risk-hubs/strategic-risk",
    element: <StrategicRiskHub />,
  },
  {
    path: "/risk-hubs/strategic-risk/*",
    element: <StrategicRiskHub />,
  },
  {
    path: "/risk-hubs/model-risk",
    element: <ModelRiskHub />,
  },
  {
    path: "/risk-hubs/model-risk/*",
    element: <ModelRiskHub />,
  },
  {
    path: "/risk-hubs/regulatory-change",
    element: <RegulatoryChangeHub />,
  },
  {
    path: "/risk-hubs/regulatory-change/*",
    element: <RegulatoryChangeHub />,
  },
  {
    path: "/risk-hubs/*",
    element: <OperationalRiskHub />, // Default hub for demo
  },
], {
  future: {
    v7_startTransition: true,
  },
});

function App() {
  return (
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  );
}

export default App; 